// TEAM OS Hub — Module Registry (frontend, code-time)
//
// Single source of truth for the App-Modules ecosystem. Each module
// declares:
//
//   key         stable identifier (kebab-case, used in URLs + i18n)
//   route       public path under /apps/
//   status      "active" | "coming_soon" | "internal_only"
//   visible     boolean — controls whether the module renders on the
//               public /apps Hub landing. Modules with visible:false
//               remain registered internally (roadmap) but are NOT
//               linked from the landing grid. We activate ONE new card
//               every ~3–4 weeks under explicit instruction from José.
//               No automatic/date-based activation.
//   activeRoute optional — where to send the user when they click "Open app"
//                          on an active module
//   icon        lucide-react icon component name
//   core        list of Core capabilities the module relies on
//   tagline     short marketing line (rendered raw — i18n via apps.<key>.tagline)
//
// NOTE: This is a code-time registry. No DB collection, no API endpoint.
// When a module graduates to "active" we just flip the status here.
// Future server-side registry (FastAPI mirror) can be added later if
// modules need cross-app activation/consent gates.

import {
  ClipboardList, Activity, Apple, Dumbbell, Plane, HeartPulse, HandCoins,
} from "lucide-react";

export const MODULES = [
  {
    key: "questionnaires",
    route: "/apps/questionnaires",
    status: "active",
    visible: true,
    activeRoute: "/app",
    icon: ClipboardList,
    accentClass: "emerald",
    core: ["auth", "users", "profiles", "i18n", "calendar", "wishlists", "marketplace", "rewards", "referrals", "consent"],
  },
  {
    key: "racket_lab",
    route: "/apps/racket-lab",
    status: "coming_soon",
    visible: true,
    icon: Activity,
    accentClass: "indigo",
    // Feb 2026 — copy refresh aligned with TEAM OS Hub vision.
    // 12 Core AI capabilities reused by Racket Lab. Order is intentional
    // (strategic potential → AI / hub / marketplace first, identity last).
    // Labels are translated via i18n key `apps.coreCaps.<token>` (see
    // appsI18n.js). Adding a new token requires adding a label there too.
    core: [
      "team_universe",
      "intelligent_management",
      "marketplace",
      "profiles",
      "wishlist",
      "calendar",
      "notifications",
      "settings",
      "community_of_users",
      "consent",
      "qr",
      "auth",
    ],
  },
  {
    key: "nutrition_lab",
    route: "/apps/nutrition-lab",
    status: "coming_soon",
    visible: false,
    icon: Apple,
    accentClass: "rose",
    core: ["auth", "users", "consent", "i18n"],
  },
  {
    key: "physical_trainer_lab",
    route: "/apps/physical-trainer-lab",
    status: "coming_soon",
    visible: false,
    icon: Dumbbell,
    accentClass: "amber",
    core: ["auth", "users", "calendar", "consent"],
  },
  {
    key: "travel_events_lab",
    route: "/apps/travel-events-lab",
    status: "coming_soon",
    visible: false,
    icon: Plane,
    accentClass: "sky",
    core: ["auth", "users", "calendar", "budget", "consent"],
  },
  {
    key: "health_habits_lab",
    route: "/apps/health-habits-lab",
    status: "coming_soon",
    visible: false,
    icon: HeartPulse,
    accentClass: "pink",
    core: ["auth", "users", "consent", "minors_protection"],
  },
  {
    key: "donations_sponsors_lab",
    route: "/apps/donations-sponsors-lab",
    status: "coming_soon",
    visible: false,
    icon: HandCoins,
    accentClass: "violet",
    core: ["auth", "users", "wishlists", "marketplace", "campaigns", "consent"],
  },
];

export const findModule = (key) => MODULES.find((m) => m.key === key);

// Public Hub helper — only modules with visible:true render on /apps.
// Hidden modules remain registered for internal/roadmap tracking and
// keep their sublanding route reachable by direct URL, but are NOT
// surfaced from the landing grid.
export const visibleModules = () => MODULES.filter((m) => m.visible === true);

// Accent class → Tailwind utility map (kept tiny to avoid PurgeCSS surprises)
export const ACCENT = {
  emerald: { bg: "bg-emerald-50",  border: "border-emerald-200", text: "text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700" },
  indigo:  { bg: "bg-indigo-50",   border: "border-indigo-200",  text: "text-indigo-700",  btn: "bg-indigo-600 hover:bg-indigo-700" },
  rose:    { bg: "bg-rose-50",     border: "border-rose-200",    text: "text-rose-700",    btn: "bg-rose-600 hover:bg-rose-700" },
  amber:   { bg: "bg-amber-50",    border: "border-amber-200",   text: "text-amber-700",   btn: "bg-amber-600 hover:bg-amber-700" },
  sky:     { bg: "bg-sky-50",      border: "border-sky-200",     text: "text-sky-700",     btn: "bg-sky-600 hover:bg-sky-700" },
  pink:    { bg: "bg-pink-50",     border: "border-pink-200",    text: "text-pink-700",    btn: "bg-pink-600 hover:bg-pink-700" },
  violet:  { bg: "bg-violet-50",   border: "border-violet-200",  text: "text-violet-700",  btn: "bg-violet-600 hover:bg-violet-700" },
};
